import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { Wortmarke } from '../komponenten/Wortmarke'

interface Tokenpruefung {
  gueltig: boolean
  email: string | null
  zweck: string | null
}

/**
 * Seite für Einladungslinks und Passwort-Zurücksetzung. Beide Wege nutzen dasselbe
 * Einmaltoken und unterscheiden sich nur in der Beschriftung.
 */
export function PasswortSetzen() {
  const token = new URLSearchParams(window.location.search).get('token') ?? ''
  const [pruefung, setPruefung] = useState<Tokenpruefung | null>(null)
  const [passwort, setPasswort] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [fertig, setFertig] = useState(false)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    if (!token) {
      setPruefung({ gueltig: false, email: null, zweck: null })
      return
    }
    api
      .get<Tokenpruefung>(`/api/zugang/pruefen?token=${encodeURIComponent(token)}`)
      .then(setPruefung)
      .catch(() => setPruefung({ gueltig: false, email: null, zweck: null }))
  }, [token])

  async function absenden(ereignis: FormEvent) {
    ereignis.preventDefault()
    setFehler(null)
    if (passwort !== wiederholung) {
      setFehler('Die beiden Passwörter stimmen nicht überein')
      return
    }
    setLaeuft(true)
    try {
      await api.post('/api/zugang/passwort-setzen', { token, passwort })
      setFertig(true)
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen')
    } finally {
      setLaeuft(false)
    }
  }

  const istEinladung = pruefung?.zweck === 'EINLADUNG'

  return (
    <div className="anmeldung">
      <div className="anmeldung-karte">
        <Wortmarke />
        <p className="anmeldung-untertitel">Multi-Tenant Bus-Operations</p>

        {pruefung === null && <div className="laden">Link wird geprüft …</div>}

        {pruefung && !pruefung.gueltig && (
          <>
            <div className="meldung fehler">
              Dieser Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen an.
            </div>
            <a href="/">
              <button className="knopf-schlicht knopf-voll">Zur Anmeldung</button>
            </a>
          </>
        )}

        {pruefung?.gueltig && fertig && (
          <>
            <div className="meldung erfolg">
              Das Passwort wurde gesetzt. Sie können sich jetzt anmelden.
            </div>
            <a href="/">
              <button className="knopf-primaer knopf-voll">Zur Anmeldung</button>
            </a>
          </>
        )}

        {pruefung?.gueltig && !fertig && (
          <>
            <h1 style={{ fontSize: '1.15rem', marginBottom: 6 }}>
              {istEinladung ? 'Willkommen bei VLZHUB' : 'Neues Passwort festlegen'}
            </h1>
            <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginBottom: 20 }}>
              {istEinladung
                ? 'Bitte vergeben Sie ein Passwort für Ihren Zugang '
                : 'Für Ihren Zugang '}
              <strong>{pruefung.email}</strong>.
            </p>

            {fehler && <div className="meldung fehler">{fehler}</div>}

            <form onSubmit={absenden}>
              <div className="feld">
                <label htmlFor="passwort">Passwort (mindestens 10 Zeichen)</label>
                <input
                  id="passwort"
                  type="password"
                  required
                  minLength={10}
                  autoComplete="new-password"
                  value={passwort}
                  onChange={(e) => setPasswort(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="feld">
                <label htmlFor="wiederholung">Passwort wiederholen</label>
                <input
                  id="wiederholung"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={wiederholung}
                  onChange={(e) => setWiederholung(e.target.value)}
                />
              </div>
              <button type="submit" className="knopf-primaer knopf-voll" disabled={laeuft}>
                {laeuft ? 'Wird gespeichert …' : 'Passwort festlegen'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
